import { useCallback, useContext, useRef } from 'react';
import { DataContext } from '../DataContext';
import { FilterContext } from '../FilterContext';
import { PageAreaContext } from '../PageAreaContext';
import { ParentContext } from '../ParentContext';
import { useViewItemStatus } from './useViewItemStatus';
import { useDataModifications } from './useDataModifications';
import { gEnums } from 'global/enums/globalEnums';

export const useItemOpts = ({ uivi }) => {

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { currentSettings_state, framework_state, page_state } = states

  const pageAreaContext = useContext(PageAreaContext)
  const { pageArea_state } = pageAreaContext ?? {}

  const filterContext = useContext(FilterContext);
  const { uiFilter_state } = filterContext ?? {}

  const dataContext = useContext(DataContext);
  const { data_state } = dataContext ?? {}

  const viewItemStatus = useViewItemStatus({ uivi })
  const { _dataModificationOpts, dmOpts } = useDataModifications()

  const infoRef = useRef({
    currentSettings_state,
    data_state,
    framework_state,
    page_state,
    pageArea_state,
    uiFilter_state,
  })

  // Update the ref on every render
  infoRef.current = {
    currentSettings_state,
    data_state,
    framework_state,
    page_state,
    pageArea_state,
    uiFilter_state,
  }

  const getAllOpts = useCallback(() => {
    const allOpts = {
      _dataOpts: getDataOpts(),
      _filterOpts: getFilterOpts(),
      _groupingOpts: getGroupingOpts(),
      _imageOpts: getImageOpts(),
      _pageOpts: getPageOpts(),
      _dataModificationOpts,
      _viewItemOpts: getViewItemOpts(),
    }
    allOpts._display = allOpts?._pageOpts?._display
    return allOpts
  }, [])

  const getViewItemOpts = useCallback(() => {
    const { pageArea_state, page_state, framework_state } = infoRef.current
    const { pageItemsActive } = pageArea_state ?? {}
    const { aps_page } = _.get(page_state, 'pageSettings', {});
    let { layout, desktopLayout } = aps_page ?? {}
    const { desktopMode } = framework_state ?? {}
    // const _layout = desktopMode ? desktopLayout : layout
    // const { layoutType } = _layout
    let viewItem = pageItemsActive?.[uivi] || {}
    const { display, desktopDisplay, search, filter } = viewItem ?? {}
    let _display = desktopMode && desktopDisplay ? desktopDisplay : display

    const { caption, displayType, cardDisplayType, showExtra, extraDisplayType, showExtraInternal } = _display ?? {}

    const { showSearch, searchOnPage } = search ?? {}
    const { showFilter } = filter ?? {}

    const _viewItemOpts = {
      caption,
      displayType,
      cardDisplayType,
      showExtra,
      extraDisplayType,
      showExtraInternal,
      searchOnPage,
      showFilter,
      showSearch
    }
    return _viewItemOpts
  }, [])

  const getPageOpts = useCallback(() => {
    const { pageArea_state, currentSettings_state, page_state, framework_state } = infoRef.current
    const { pageItemsActive } = pageArea_state ?? {}
    const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
    const { desktop } = aps_global ?? {}
    const { aps_page } = _.get(page_state, 'pageSettings', {});
    const { layout } = aps_page ?? {}
    const { desktopMode } = framework_state ?? {}
    const _layout = desktopMode ? desktopLayout : layout
    const { layoutType } = _layout ?? {}
    const viewItem = pageItemsActive ? pageItemsActive[uivi] : {}
    const { display, desktopDisplay } = viewItem ?? {}
    const { itemWrapType } = desktop ?? {}
    let _display = desktopMode && desktopDisplay ? desktopDisplay : display
    const _pageOpts = {
      _display,
      itemWrapType,
      layout,
      layoutType
    }
    return _pageOpts
  }, [])

  const getFilterOpts = useCallback(() => {
    const { uiFilter_state } = infoRef.current
    const {
      alphaValue,
      alphabetActive,
      divideIntoGroups,
      filterSidebarOpen,
      filterStatusType,
      filterValues,
      groups,
      searchValue,
      selectedFilters,
      dataFilters,
    } = uiFilter_state

    const _filterOpts = {
      alphaValue,
      alphabetActive,
      divideIntoGroups,
      filterSidebarOpen,
      filterStatusType,
      filterValues,
      groups,
      searchValue,
      selectedFilters,
      dataFilters,
    }
    return _filterOpts
  }, [])

  const getDataOpts = useCallback(() => {
    const { data_state } = infoRef.current

    const {
      isCombinedData,
      viewListData,
      viewListDataCount,
      time_stamp,
      alphabetValue
    } = data_state

    const _dataOpts = {
      isCombinedData,
      uivi,
      viewListData,
      viewListDataCount,
      time_stamp,
      alphabetValue
    }

    return _dataOpts

  }, [])

  const getGroupingOpts = useCallback(() => {
    const { currentSettings_state, pageArea_state, data_state } = infoRef.current
    const { pageItemsActive } = pageArea_state ?? {}
    const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
    const { groupingOptions } = aps_global ?? {}
    let viewItem = pageItemsActive ? pageItemsActive[uivi] : {}
    const { grouping } = viewItem ?? {}
    const { viewListDataCount } = data_state
    const opts = getGroupOptions({
      uivi,
      grouping,
      groupingOptions,
      viewItemStatus,
      viewListDataCount
    })
    return opts
  }, [])

  const getImageOpts = useCallback(() => {
    const { currentSettings_state, pageArea_state } = infoRef.current
    const { pageItemsActive } = pageArea_state ?? {}
    const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
    const { imageOptions } = aps_global ?? {}
    let viewItem = pageItemsActive ? pageItemsActive[uivi] : {}
    const { images } = viewItem ?? {}
    const opts = getImageOptions({
      uivi,
      images,
      imageOptions
    })
    return opts
  }, [])

  return { getAllOpts, getPageOpts, getFilterOpts, getDataOpts, getGroupingOpts, getImageOpts }

}

const getGroupOptions = ({ uivi, grouping, groupingOptions, viewItemStatus, viewListDataCount }) => {

  const { isViewItemSolo, isViewItemList, isViewItemSubList } = viewItemStatus ?? {}
  const _isList = isViewItemList || isViewItemSubList

  const _groupingProps = ['groupType', 'orderGroupsByAlpha', 'useStartCaseHeaders']

  const { overrideAlphaSettings, overrideGroupSettings } = grouping ?? {}
  const { useGlobalAlphabetSettings, useGlobalGroupSettings, applyToCollections, alphaFilterType, alphaFilterAfter } = groupingOptions ?? {}

  const _alphaFilterAfter = alphaFilterAfter ? alphaFilterAfter : 50

  let _grouping = _.clone(grouping)

  if (useGlobalAlphabetSettings && !overrideAlphaSettings && applyToCollections && applyToCollections.includes(uivi) && _isList) {
    if (!_grouping) {
      _grouping = {
        origin: 'aui'
      }
    }
    switch (alphaFilterType) {
      case gEnums.alphaFilterTypes.afterLimit:
        _grouping.alphabetActive = viewListDataCount > _alphaFilterAfter
        break;
      default:
        _grouping.alphabetActive = true
    }
  }

  if (_grouping && groupingOptions) {
    if (useGlobalGroupSettings && !overrideGroupSettings) {
      Object.keys(_grouping).forEach(gKey => {
        if (_groupingProps.includes(gKey)) {
          _grouping[gKey] = groupingOptions[gKey] ? groupingOptions[gKey] : _grouping[gKey]
        }
      })
    }

    if (useGlobalAlphabetSettings && !overrideAlphaSettings) {
      Object.keys(groupingOptions).forEach(gKey => {
        if (!_groupingProps.includes(gKey)) {
          _grouping[gKey] = groupingOptions[gKey] ? groupingOptions[gKey] : _grouping[gKey]
        }
      })
    }
  }

  const _groupingOpts = { ..._grouping }

  return isViewItemSolo ? {} : _groupingOpts
}

const getImageOptions = ({ uivi, images, imageOptions }) => {

  let _images_vi = _.clone(images)

  const { overrideImageSettings } = _images_vi ?? {}
  const { useGlobalImageSettings, collectionImageCollections } = imageOptions ?? {}

  let _imageOptions = {}

  if (useGlobalImageSettings && collectionImageCollections && collectionImageCollections.includes(uivi) && !overrideImageSettings) {
    _imageOptions = overrideImageSettings ? _images_vi : imageOptions
    _imageOptions.showItemImage = true
  } else {
    _imageOptions = _images_vi || {}
  }

  return _imageOptions
}